import { useQuery } from '@tanstack/react-query'
import { KeyRound, Lock, ShieldAlert, Users } from 'lucide-react'
import { Link } from 'react-router-dom'

import { ErrorState } from '@/components/feedback/ErrorState'
import { Loading } from '@/components/feedback/Loading'
import { StatCard } from '@/components/stat/StatCard'
import { Badge } from '@/components/ui/badge'
import { Card } from '@/components/ui/card'
import { useReports } from '@/hooks/use-cube-meta'
import { formatCubeDateTime, formatDateTime, formatInteger } from '@/lib/format'
import { messages } from '@/lib/messages'
import { queryKeys } from '@/lib/query-keys'
import { cn } from '@/lib/utils'
import { services } from '@/services'
import type { RealtimeStatus } from '@/services/contracts'
import { useAuthStore } from '@/stores/auth-store'
import { useRealtimeStore } from '@/stores/realtime-store'
import type { SystemSourcesResponse } from '@/types/models'

const text = messages.overview

/** Kart tonu: durum kartının kenarı ve rozeti. `neutral` = eşik tanımsız, renklenmez (Q2). */
type Tone = 'ok' | 'warning' | 'danger' | 'neutral'

const TONE_BADGE: Record<Tone, 'success' | 'warning' | 'danger' | 'neutral'> = {
  ok: 'success',
  warning: 'warning',
  danger: 'danger',
  neutral: 'neutral',
}

function StatusCard({
  title,
  tone,
  status,
  detail,
  to,
}: {
  title: string
  tone: Tone
  status: string
  detail?: string
  to?: string
}) {
  const body = (
    <Card
      className={cn(
        'flex h-full flex-col gap-2 p-4',
        tone === 'warning' && 'border-warning',
        tone === 'danger' && 'border-danger',
        to && 'hover:bg-surface-muted transition-colors',
      )}
    >
      {/* Dar ızgarada rozet başlığın altına iner, kesilmez. */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="min-w-0 text-sm font-medium">{title}</span>
        <Badge variant={TONE_BADGE[tone]} className="shrink-0">
          {status}
        </Badge>
      </div>
      {detail && <p className="text-text-secondary text-xs">{detail}</p>}
    </Card>
  )
  return to ? (
    <Link
      to={to}
      className="focus-visible:ring-ring rounded-xl outline-hidden focus-visible:ring-2"
    >
      {body}
    </Link>
  ) : (
    body
  )
}

/** En yeni `son_sync`: kaynak naive İstanbul saati verir, metin olarak karşılaştırılır. */
function latestSync(data: SystemSourcesResponse): string | null {
  const values = data.sources
    .flatMap((source) => source.sync_durum.map((row) => row.son_sync))
    .filter((value): value is string => !!value)
  return values.length ? values.reduce((a, b) => (a > b ? a : b)) : null
}

function SourceCards({ data }: { data: SystemSourcesResponse }) {
  const statuses = data.sources.map((source) => source.status)
  const tone: Tone = statuses.some((s) => s === 'unreachable')
    ? 'danger'
    : statuses.some((s) => s === 'schema_mismatch')
      ? 'warning'
      : statuses.every((s) => s === 'ok')
        ? 'ok'
        : 'neutral'
  const lastSync = latestSync(data)
  // Çalışıyor ama son yoklama hata verdi (ör. kaynak erişilemiyor): sarı, hata açıklamada.
  const realtimeTone: Tone = !data.realtime.watcher_running
    ? 'danger'
    : data.realtime.last_error
      ? 'warning'
      : 'ok'

  return (
    <>
      <StatusCard
        title={text.sources}
        tone={tone}
        status={
          data.sources.length === 1
            ? messages.sources.status[data.sources[0].status]
            : text.sourcesSummary(statuses.filter((s) => s === 'ok').length, statuses.length)
        }
        detail={data.sources.map((source) => source.label).join(', ')}
        to="/yonetim/kaynaklar"
      />
      <StatusCard
        title={text.sync}
        tone="neutral"
        status={lastSync ? formatCubeDateTime(lastSync) : text.noSync}
        detail={text.syncHint}
        to="/yonetim/kaynaklar"
      />
      <StatusCard
        title={text.watcher}
        tone={realtimeTone}
        status={data.realtime.watcher_running ? text.running : text.stopped}
        detail={
          data.realtime.last_error
            ? text.lastError(data.realtime.last_error)
            : data.realtime.last_poll_at
              ? text.lastPoll(formatDateTime(data.realtime.last_poll_at))
              : undefined
        }
        to="/yonetim/kaynaklar"
      />
    </>
  )
}

/** Tarayıcının kendi Cube erişimi: rapor meta isteğinin durumu. */
function CubeCard() {
  const reports = useReports()
  const tone: Tone = reports.isError ? 'danger' : reports.isSuccess ? 'ok' : 'neutral'
  return (
    <StatusCard
      title={text.cube}
      tone={tone}
      status={
        reports.isError ? text.unreachable : reports.isSuccess ? text.reachable : text.checking
      }
      detail={reports.isSuccess ? text.reportCount(reports.data.length) : undefined}
    />
  )
}

const SSE_LABEL: Record<RealtimeStatus, string> = {
  open: messages.realtime.live,
  connecting: messages.realtime.connecting,
  reconnecting: messages.realtime.reconnecting,
  closed: messages.realtime.offline,
}

/** Bu tarayıcının SSE bağlantısı (`hooks/use-realtime`). */
function SseCard() {
  const status = useRealtimeStore((state) => state.status)
  const tone: Tone =
    status === 'open'
      ? 'ok'
      : status === 'reconnecting'
        ? 'warning'
        : status === 'closed'
          ? 'danger'
          : 'neutral'
  return (
    <StatusCard title={text.sse} tone={tone} status={SSE_LABEL[status]} detail={text.sseHint} />
  )
}

/** Yönetim genel bakışı (docs/ADMIN-PANEL-PLAN.md Faz 6); her blok kendi iznine göre görünür (Q1). */
export function OverviewPage() {
  const identity = useAuthStore((state) => state.identity)
  const can = (key: string) =>
    !!identity && (identity.is_superadmin || identity.permissions.includes(key))

  const overviewQuery = useQuery({
    queryKey: queryKeys.admin.overview(),
    queryFn: () => services.system.adminOverview(),
    refetchInterval: 60_000,
  })
  const sourcesQuery = useQuery({
    queryKey: queryKeys.systemSources(),
    queryFn: () => services.system.sources(),
    enabled: can('admin.sources:view'),
    refetchInterval: 30_000,
  })
  const users = overviewQuery.data?.users

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-lg font-semibold">{text.title}</h1>
        <p className="text-muted-foreground text-sm">{text.description}</p>
      </div>

      {overviewQuery.isError ? (
        <ErrorState error={overviewQuery.error} onRetry={() => void overviewQuery.refetch()} />
      ) : overviewQuery.isLoading ? (
        <Loading rows={2} />
      ) : (
        users && (
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <StatCard
              label={text.activeUsers}
              icon={<Users aria-hidden="true" />}
              value={formatInteger(users.active_users)}
              hint={text.inactiveUsers(users.inactive_users)}
            />
            <StatCard
              label={text.lockedUsers}
              icon={<Lock aria-hidden="true" />}
              value={formatInteger(users.locked_users)}
              valueClassName={users.locked_users > 0 ? 'text-warning' : undefined}
            />
            <StatCard
              label={text.failedLogins}
              icon={<ShieldAlert aria-hidden="true" />}
              value={formatInteger(users.failed_logins_24h)}
            />
            <StatCard
              label={text.mcpConnections}
              icon={<KeyRound aria-hidden="true" />}
              value={formatInteger(users.open_mcp_connections)}
            />
          </div>
        )
      )}

      <section className="flex flex-col gap-3">
        <h2 className="text-text-secondary text-sm font-medium">{text.statusTitle}</h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {can('admin.sources:view') &&
            (sourcesQuery.isError ? (
              <StatusCard
                title={text.sources}
                tone="danger"
                status={text.unreachable}
                to="/yonetim/kaynaklar"
              />
            ) : sourcesQuery.data ? (
              <SourceCards data={sourcesQuery.data} />
            ) : (
              <Loading rows={1} />
            ))}
          <CubeCard />
          <SseCard />
        </div>
      </section>
    </div>
  )
}
